/**
 * Совпадает с логикой DashboardPage.tsx в client-frontend:
 * вкладки «Заказы» и «Операции», формат сумм Intl и подписей статусов.
 */

import { parseMskInstant } from '../../utils/moscow-calendar-date';

export const CABINET_HISTORY_PAGE_SIZE = 10;

const ORDER_STATUS_LABEL_RU = {
  draft: 'Черновик',
  waiting_for_capacity: 'Ожидание емкости',
  reserved_for_payment: 'Резерв готов к оплате',
  awaiting_payment: 'Ожидает оплаты',
  paid: 'Оплачен',
  queued_for_provisioning: 'В очереди на выдачу',
  processing: 'Выдача в процессе',
  active: 'Покупка',
  support_required: 'Нужна поддержка',
  payment_failed: 'Оплата неуспешна',
  payment_expired: 'Истекло время оплаты',
  cancelled: 'Отменен',
  refund_required: 'Требует возврата',
  refunded: 'Возвращен',
};

/** @param {unknown} iso */
export const formatCabinetHistoryOrderDate = (iso) => {
  const date = parseMskInstant(iso);
  if (!date) {
    return '—';
  }
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone: 'Europe/Moscow',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
};

/** @param {number|string} value @param {string} [currency] */
export const formatCabinetHistoryAmount = (value, currency) => {
  const cur = currency === 'USD' ? 'USD' : 'RUB';
  return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: cur, maximumFractionDigits: 2 }).format(
    Number(value) || 0,
  );
};

const REFUNDABLE_DEBIT_ORDER_TYPES = new Set([
  'new_purchase',
  'renewal',
  'balance_auto_renewal',
  'cabinet_topup',
]);
const REFUNDABLE_ORDER_STATUSES = new Set(['active', 'paid', 'processing', 'queued_for_provisioning']);
const BLOCKED_REFUND_STATUSES = new Set(['refunded', 'refund_required', 'cancelled']);
/** Возврат из операций — только оплата через АТОЛ (не cabinet_balance / админский баланс). */
const REFUNDABLE_PAYMENT_PROVIDER = 'atol_pay';

/**
 * Доступность возврата по заказу (дублирует backend, если API ещё без can_refund).
 * @param {Record<string, unknown>} order
 */
export const inferOrderCanRefund = (order) => {
  const provider = String(order.payment_provider ?? order.refund_provider ?? '')
    .trim()
    .toLowerCase();

  if (provider === 'cabinet_balance') {
    return false;
  }

  if (order.can_refund === true) {
    return provider === REFUNDABLE_PAYMENT_PROVIDER || provider === '';
  }
  if (order.can_refund === false && (order.refund_provider != null || order.payment_provider != null)) {
    return false;
  }

  const statusNorm = String(order.status ?? '').toLowerCase();
  if (BLOCKED_REFUND_STATUSES.has(statusNorm)) {
    return false;
  }

  const orderType = String(order.order_type ?? '').toLowerCase();
  if (!REFUNDABLE_DEBIT_ORDER_TYPES.has(orderType)) {
    return false;
  }

  if (orderType === 'cabinet_topup') {
    if (statusNorm !== 'paid') {
      return false;
    }
  } else if (!REFUNDABLE_ORDER_STATUSES.has(statusNorm)) {
    return false;
  }

  const amount = Number(order.total_amount);
  if (!Number.isFinite(amount) || amount <= 0) {
    return false;
  }

  return provider === REFUNDABLE_PAYMENT_PROVIDER;
};

/** @param {Record<string, unknown>} order */
export const inferOrderRefundProvider = (order) => {
  const explicit = order.refund_provider ?? order.payment_provider;
  if (explicit != null && String(explicit).trim() !== '') {
    return String(explicit).trim();
  }
  return null;
};

/** Зависший заказ покупки/продления — можно отменить с возвратом на баланс ЛК. */
export const inferOrderCanCancelStuck = (order) => {
  if (order.can_cancel_stuck === true) {
    return true;
  }
  if (order.can_cancel_stuck === false) {
    return false;
  }

  const orderType = String(order.order_type ?? '').toLowerCase();
  if (orderType !== 'new_purchase' && orderType !== 'renewal') {
    return false;
  }

  const statusNorm = String(order.status ?? '').toLowerCase();
  if (
    ['refunded', 'cancelled', 'refund_required', 'draft', 'awaiting_payment', 'reserved_for_payment'].includes(
      statusNorm,
    )
  ) {
    return false;
  }

  const pipeline = ['queued_for_provisioning', 'processing', 'support_required', 'paid'];
  if (pipeline.includes(statusNorm)) {
    return true;
  }

  if (orderType === 'new_purchase' && statusNorm === 'active') {
    const active = Number(order.provisioned_active_count ?? 0);
    const inactive = Number(order.provisioned_inactive_count ?? 0);
    return active + inactive === 0;
  }

  if (orderType === 'renewal') {
    const qStatus = String(order.provisioning_queue_status ?? '').toLowerCase();
    return ['queued', 'locked', 'running', 'retry_wait', 'support_required', 'failed'].includes(qStatus);
  }

  return false;
};

/**
 * Статус строки заказа для бейджей (таблица и карточка заказа).
 * @param {Record<string, unknown>} order объект заказа из API (список или карточка)
 */
export const cabinetPurchaseStatusPartsFromOrder = (order) => {
  const statusNorm = String(order.status || '').toLowerCase();
  const needsPay = statusNorm === 'reserved_for_payment' || statusNorm === 'awaiting_payment';
  const orderType = String(order.order_type ?? '').toLowerCase();
  let statusBadgeKind =
    orderType === 'renewal' ? 'renewal' : orderType === 'balance_auto_renewal' ? 'auto_renewal' : 'default';
  if (statusNorm === 'refunded') {
    statusBadgeKind = 'refunded';
  } else if (statusNorm === 'payment_failed') {
    statusBadgeKind = 'payment_failed';
  } else if (statusNorm === 'cancelled') {
    statusBadgeKind = 'cancelled';
  }
  const rawStatusLabel =
    orderType === 'admin_manual_debit'
      ? 'Списание (Админ)'
      : orderType === 'admin_manual_topup'
        ? 'Пополнение (Админ)'
        : orderType === 'balance_auto_renewal'
          ? 'Автопродление'
          : orderType === 'renewal'
            ? 'Продление'
            : orderType === 'new_purchase' && Number(order.provisioning_partial_refund_rub) > 0
              ? 'Покупка (частично)'
            : ORDER_STATUS_LABEL_RU[statusNorm] ?? String(order.status || '');
  return {
    statusLabel: rawStatusLabel,
    statusTone: needsPay ? 'pending' : 'ok',
    statusBadgeKind,
  };
};

/** Человекочитаемый тип заказа (как orderTypeLabelRu в client-frontend). */
export const orderTypeLabelRu = (orderType) => {
  const key = String(orderType ?? '').trim().toLowerCase();
  const map = {
    new_purchase: 'Новая покупка',
    renewal: 'Продление',
    replacement: 'Замена',
    cabinet_topup: 'Пополнение баланса',
    admin_manual_topup: 'Ручное зачисление',
    admin_manual_debit: 'Ручное списание',
    admin_manual_refund: 'Возврат на баланс (Админ)',
    balance_auto_renewal: 'Автопродление с баланса',
    provisioning_refund_credit: 'Зачисление (незавершённая выдача)',
  };
  if (!key) {
    return '—';
  }
  return map[key] ?? String(orderType ?? '');
};

const PAYMENT_STATUS_LABEL_RU = {
  created: 'Создано',
  awaiting_payment: 'Ожидает оплаты',
  paid: 'Оплачено',
  expired: 'Истекло',
  failed: 'Ошибка оплаты',
};

/** @param {string|undefined|null} paymentStatus */
export const paymentStatusLabelRu = (paymentStatus) => {
  const k = String(paymentStatus ?? '').trim().toLowerCase();
  return PAYMENT_STATUS_LABEL_RU[k] ?? String(paymentStatus || '—');
};

/** @param {string|undefined|null} iso */
export const formatCabinetHistoryDateTime = (iso) => {
  if (!iso) {
    return '—';
  }
  const date = parseMskInstant(iso);
  if (!date) {
    return '—';
  }
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone: 'Europe/Moscow',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

/** @param {Record<string, unknown>} order элемент items из биллинга */
export const mapCabinetBillingOrderToPurchaseRow = (order) => {
  const { statusLabel, statusTone, statusBadgeKind } = cabinetPurchaseStatusPartsFromOrder(order);

  const cur = order.currency === 'USD' ? 'USD' : 'RUB';
  const items = Array.isArray(order.items) ? order.items : [];
  const ordered =
    order.ordered_proxy_quantity != null
      ? Number(order.ordered_proxy_quantity)
      : items.reduce((s, it) => s + (Number(it?.quantity) || 0), 0);
  const refundAmountRub = Math.abs(Number(order.total_amount) || 0);
  const statusNorm = String(order.status ?? '').toLowerCase();
  const isCancelled = statusNorm === 'cancelled';

  return {
    orderId: order.id,
    orderNumber: String(order.order_number || ''),
    date: formatCabinetHistoryOrderDate(order.created_at),
    amountFormatted: formatCabinetHistoryAmount(order.total_amount, cur),
    statusLabel,
    statusTone,
    statusBadgeKind,
    proxies: String(ordered),
    active: String(order.provisioned_active_count ?? 0),
    inactive: String(order.provisioned_inactive_count ?? 0),
    tariffName: order.flexible_tariff_name ?? '—',
    canCancelStuck: inferOrderCanCancelStuck(order),
    isCancelled,
    cancelAmountFormatted: formatCabinetHistoryAmount(refundAmountRub, cur),
    cancelAmountRub: refundAmountRub,
  };
};

/** @param {Record<string, unknown>[]} rawItems ответ GET orders */
export const purchaseHistoryRowsFromBillingItems = (rawItems) =>
  rawItems
    .filter((o) => {
      const t = String(o.order_type ?? '').toLowerCase();
      return t === 'new_purchase' || t === 'renewal' || t === 'balance_auto_renewal';
    })
    .map(mapCabinetBillingOrderToPurchaseRow);

/** Подпись выполненного возврата по способу оплаты. */
const refundDoneLabelFromOrder = (order) => {
  const provider = String(order.payment_provider ?? order.refund_provider ?? '')
    .trim()
    .toLowerCase();
  if (provider === 'atol_pay') {
    return 'Возврат (АТОЛ)';
  }
  if (provider === 'cabinet_balance') {
    return 'Возврат на баланс';
  }
  return 'Возвращено';
};

/** @param {Record<string, unknown>[]} rawItems */
export const operationHistoryRowsFromBillingItems = (rawItems) => {
  const settled = new Set(['paid']);
  const orderDebitSettled = new Set([
    'active',
    'paid',
    'processing',
    'queued_for_provisioning',
    'refunded',
  ]);
  return rawItems
    .filter((order) => {
      const t = String(order.order_type ?? '').toLowerCase();
      const st = String(order.status ?? '').toLowerCase();
      if (t === 'cabinet_topup') {
        return settled.has(st) || st === 'refunded';
      }
      if (t === 'admin_manual_topup' || t === 'admin_manual_debit' || t === 'admin_manual_refund') {
        return settled.has(st);
      }
      if (t === 'provisioning_refund_credit') {
        return settled.has(st);
      }
      return (
        (t === 'new_purchase' || t === 'renewal' || t === 'balance_auto_renewal') &&
        orderDebitSettled.has(st)
      );
    })
    .map((order) => {
      const t = String(order.order_type ?? '').toLowerCase();
      const statusNorm = String(order.status ?? '').toLowerCase();
      const isRefunded = statusNorm === 'refunded';
      const isTopup =
        t === 'cabinet_topup' ||
        t === 'admin_manual_topup' ||
        t === 'admin_manual_refund' ||
        t === 'provisioning_refund_credit';
      const isManualDebit = t === 'admin_manual_debit';
      const isPurchase = t === 'new_purchase';
      const isRenewal = t === 'renewal';
      const isAutoRenewal = t === 'balance_auto_renewal';
      const typeLabel = isTopup
        ? t === 'admin_manual_topup'
          ? 'Пополнение (Админ)'
          : t === 'admin_manual_refund'
            ? 'Возврат (Админ)'
          : t === 'provisioning_refund_credit'
            ? 'Возврат на баланс (прокси)'
          : 'Пополнение счёта'
        : isManualDebit
          ? 'Списание (Админ)'
          : isPurchase
            ? 'Списание (Покупка)'
            : isRenewal
              ? 'Списание (Продление)'
              : isAutoRenewal
                ? 'Списание (Автопродление)'
          : 'Пополнение счёта';
      const opBadgeCls = isTopup
        ? 'client-detail-drawer__op-badge client-detail-drawer__op-badge--green'
        : 'client-detail-drawer__op-badge client-detail-drawer__op-badge--red';
      const cur = order.currency === 'USD' ? 'USD' : 'RUB';
      const sign = isTopup ? '+' : '-';
      const refundAmountRub = Math.abs(Number(order.total_amount) || 0);
      return {
        key: `${order.id}-${order.created_at}`,
        orderId: Number(order.id),
        date: formatCabinetHistoryOrderDate(order.created_at),
        typeLabel,
        opBadgeCls,
        amountFormatted: `${sign}${formatCabinetHistoryAmount(order.total_amount, cur)}`,
        refundAmountRub,
        refundAmountFormatted: formatCabinetHistoryAmount(refundAmountRub, cur),
        canRefund: inferOrderCanRefund(order),
        refundProvider: inferOrderRefundProvider(order),
        isCabinetTopup: t === 'cabinet_topup',
        isRefunded,
        refundDoneLabel: isRefunded ? refundDoneLabelFromOrder(order) : null,
      };
    });
};

/** По названию тарифа как в tariffChipClassFromName (Dashboard). */
export const tariffTariffKindFromCabinetName = (name) => {
  const s = String(name || '');
  if (s.includes('Индивид')) {
    return 'individual';
  }
  if (s.includes('Общий +') || s.includes('Общий+')) {
    return 'common_plus';
  }
  return 'common';
};
